// The Accelerator's Resources, as Accelerator.dc.html draws its shelf: the heading with a tab per
// section and its count under it, the terms link on the right ("The Property Accelerator terms and
// conditions", terms 2.2), the items 3 to a row, each a card that is itself the link (no button),
// with its icon, title and line ("Calculator", "Guide", "New, added 23 Sep"), and the footnote under
// them. The rules are the shared ones (src/product/release.js): a section with nothing open is
// hidden, a link to another product in the hub (the masterclass card) shows only to someone who has
// it open, and each kind does its own thing (src/product/resourceAction.js): a link opens in a new
// tab (a page in the hub opens here), a video plays above the shelf, a file downloads through a link
// signed for 2 minutes, a tool opens its page in the hub, text copies. While staff view as a member,
// files and tools don't open, with the shared line.
//
// It keeps the shared Resources' class names for its tabs, terms link, cards and footnote (rs-tab,
// rs-terms, rs-card, rs-title, rs-meta, rs-foot), so the frame's checks of the Accelerator's
// Resources find them, and draws them in the board's own sizes (accelerator.css, .acc-shelf).
//
// The Accelerator critique (20 Oct): each card has its kind's icon and a line saying what a tap does ("Downloads",
// "Opens in a new tab", "Opens here", "Plays here", "Copies the text"), after "New, added 23 Sep" where it's new, and on
// a phone the terms link sits under the footnote (member 15); the page opens on the tab its link names
// (/accelerator/resources#guides), from the home's shortcuts (ResourceShortcuts, member 5).
//
//   <Shelf product={productRow} />                    the shelf, with an h2
//   <Shelf product={productRow} headingLevel="h1" />  the /accelerator/resources page
//   <ResourceShortcuts product={productRow} />        the home's Resources: a shortcut to each tab, with its count
import { useEffect, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { useNow } from '../../lib/clock.js'
import { usePhone } from '../../lib/media.js'
import { useProductData } from '../../product/ProductData.jsx'
import { useOwnedPaths } from '../../product/Resources.jsx'
import { VideoPanel } from '../../product/ResourceButton.jsx'
import { useResourceAction } from '../../product/resourceAction.js'
import { resourceTabs, termsLink } from '../../product/release.js'
import { KindIcon, LockLine } from '../../product/Icons.jsx'
import { COPY } from './copy.js'

// A tab's name in a link: "Calculators" -> "calculators" (/accelerator/resources#calculators)
export const tabSlug = name => String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

// The tab a link names, from the address's #, or null
function tabFromHash(tabs) {
  const hash = typeof window === 'undefined' ? '' : decodeURIComponent(window.location.hash.replace(/^#/, ''))
  return hash ? tabs.find(t => tabSlug(t.name) === hash)?.key || null : null
}

// What a tap on an open card does, in words (member 15)
export function tapLine(card) {
  const kind = card?.action?.kind
  if (kind === 'link') return card.url && card.url.startsWith('/') ? COPY.tapLine.here : COPY.tapLine.link
  return COPY.tapLine[kind] || null
}

// The home's Resources (member 5, 20 Oct): in place of the whole shelf, a shortcut to each tab with its count, each
// opening the Resources page on that tab. None while nothing is placed for them, as the shelf.
export function ResourceShortcuts({ product }) {
  const { data } = useProductData()
  const nowMs = useNow()
  const owned = useOwnedPaths()
  const tabs = data ? resourceTabs(data, nowMs, owned) : []
  if (!tabs.length) return null
  const p = data.product || product
  return (
    <section className="acc-card acc-shortcuts" aria-labelledby="acc-shortcuts-h">
      <h2 className="acc-h2" id="acc-shortcuts-h">{COPY.resources}</h2>
      <div className="acc-shortcut-grid">
        {tabs.map(t => (
          <Link key={t.key} className="acc-shortcut" to={`${p.path || ''}/resources#${tabSlug(t.name)}`}>
            <span className="acc-shortcut-name">{t.name}</span>
            <span className="acc-shortcut-count">{t.count}</span>
          </Link>
        ))}
      </div>
    </section>
  )
}

export function Shelf({ product, headingLevel = 'h2', empty = null }) {
  const { data } = useProductData()
  const nowMs = useNow()
  const owned = useOwnedPaths()
  const phone = usePhone()
  const tabs = data ? resourceTabs(data, nowMs, owned) : []
  const [pick, setPick] = useState(() => tabFromHash(tabs))
  const [playing, setPlaying] = useState(null)
  // a tab's link followed while the page is open (#guides typed or pressed again)
  const keys = tabs.map(t => `${t.key}:${t.name}`).join('|')
  useEffect(() => {
    const onHash = () => { const k = tabFromHash(tabs); if (k) { setPick(k); setPlaying(null) } }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys])
  if (!tabs.length) return empty
  const p = data.product || product
  const tab = tabs.find(t => t.key === pick) || tabs[0]
  const terms = termsLink(p)
  const footnote = p.settings?.resources?.footnote || null
  const video = playing ? tab.cards.find(c => c.id === playing) : null
  const H = headingLevel

  return (
    <section className={headingLevel === 'h1' ? 'acc-shelf acc-shelf--page' : 'acc-shelf'} aria-label="Resources">
      <div className="acc-shelf-head">
        <div className="acc-shelf-titles">
          <H className="acc-shelf-h">Resources</H>
          <div className="acc-shelf-tabs" role="tablist" aria-label="Resources">
            {tabs.map(t => (
              <button key={t.key} type="button" role="tab" aria-selected={t.key === tab.key}
                className={t.key === tab.key ? 'rs-tab acc-tab acc-tab--on' : 'rs-tab acc-tab'}
                onClick={() => { setPick(t.key); setPlaying(null) }}>
                {t.name}<span className="acc-tab-count">{t.count}</span>
              </button>
            ))}
          </div>
        </div>
        {terms && !phone && <a className="rs-terms acc-terms" href={terms.href} target="_blank" rel="noopener noreferrer">{terms.label}</a>}
      </div>
      {tab.note && <p className="acc-shelf-note">{tab.note}</p>}
      {video && <div className="acc-shelf-video"><VideoPanel card={video} onClose={() => setPlaying(null)} /></div>}
      <div className="acc-shelf-grid" role="tabpanel">
        {tab.cards.map(c => <ShelfCard key={c.id} card={c} playing={playing === c.id}
          onVideo={() => setPlaying(playing === c.id ? null : c.id)} />)}
      </div>
      {footnote && <p className="rs-foot acc-shelf-foot">{footnote}</p>}
      {terms && phone && <a className="rs-terms acc-terms acc-terms--phone" href={terms.href} target="_blank" rel="noopener noreferrer">{terms.label}</a>}
    </section>
  )
}

// 1 item: the card is the link or the button, with its kind's icon and what a tap does
function ShelfCard({ card, playing, onVideo }) {
  const act = useResourceAction(card)
  const tap = tapLine(card)
  const line = card.isNew ? [card.meta, tap].filter(Boolean).join('. ') : tap || card.meta
  const inner = (
    <>
      <span className="acc-res-icon">{card.open ? <KindIcon kind={card.kind} size={17} /> : <LockLine size={17} />}</span>
      <span className="acc-res-text">
        <span className="rs-title acc-res-title">{card.title}</span>
        <span className={card.isNew ? 'rs-meta acc-res-meta acc-res-meta--new' : 'rs-meta acc-res-meta'}>
          {card.open ? (doneLine(card, act) || line) : card.when}
        </span>
        {card.forLife && <span className="acc-res-life">Yours for life</span>}
        {act.line && <span className="acc-res-line" role="alert">{act.line}</span>}
      </span>
    </>
  )
  if (!card.open || !card.action) return <div className="rs-card acc-res acc-res--locked">{inner}</div>
  const kind = card.action.kind
  if (kind === 'link' && card.url && card.url.startsWith('/')) {
    return <Link className="rs-card acc-res" to={card.url} onClick={act.run}>{inner}</Link>
  }
  if (kind === 'link' && card.url) {
    return <a className="rs-card acc-res" href={card.url} target="_blank" rel="noopener noreferrer" onClick={act.run}>{inner}</a>
  }
  if (kind === 'tool' && card.href && !act.blocked) {
    return <a className="rs-card acc-res" href={card.href} target="_blank" rel="noopener" onClick={act.run}>{inner}</a>
  }
  function click(e) {
    act.run(e)
    if (kind === 'video') onVideo()
  }
  return (
    <button type="button" className={playing ? 'rs-card acc-res acc-res--on' : 'rs-card acc-res'} onClick={click}
      aria-busy={act.status === 'busy' || undefined} aria-expanded={kind === 'video' ? playing : undefined}>
      {inner}
    </button>
  )
}

// After a download or a copy, the card says so in the shared buttons' words
function doneLine(card, act) {
  if (act.status !== 'done') return null
  if (card.action.kind === 'download' || card.action.kind === 'copy') return card.action.doneLabel
  return null
}
